import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, ChevronRight, Cpu, Heart, ExternalLink } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../../components/ui/card';
import { cn } from '../../../../../lib/utils';

function SkillTreeNodePreview({ node, treeData, expanded, onToggle, depth = 0 }) {
  const isExpanded = expanded.includes(node.itemId);
  const nodeChildren = treeData.filter((item) => node.children.includes(item.itemId));
  const hasChildren = node.children.length > 0;

  // Count all tasks in this node and its descendants
  const getTotalTaskCount = useCallback(
    (currentNode) => {
      let count = currentNode.details.length;
      currentNode.children.forEach((childId) => {
        const childNode = treeData.find((n) => n.itemId === childId);
        if (childNode) {
          count += getTotalTaskCount(childNode);
        }
      });
      return count;
    },
    [treeData],
  );

  const totalTasks = useMemo(() => getTotalTaskCount(node), [getTotalTaskCount, node]);

  return (
    <div
      className={cn('border rounded-lg bg-card', depth > 0 && 'ml-6')}
      style={{ borderColor: 'hsl(var(--border))' }}
    >
      {/* Node Header */}
      <button
        type="button"
        onClick={() => onToggle(node.itemId)}
        className="flex items-center gap-2 w-full p-3 text-left hover:bg-muted/30 transition-colors"
      >
        {/* Expand/Collapse Icon */}
        {(hasChildren || node.details.length > 0) && isExpanded && (
          <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        )}
        {(hasChildren || node.details.length > 0) && !isExpanded && (
          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        )}
        {!hasChildren && node.details.length === 0 && <div className="w-4" />}

        {/* Node Name */}
        <span className="font-medium text-sm flex-1">{node.name}</span>

        {/* Task Count */}
        {totalTasks > 0 && <span className="text-xs text-muted-foreground">({totalTasks})</span>}
      </button>

      {/* Children or Details */}
      {isExpanded && (
        <div className="border-t" style={{ borderColor: 'hsl(var(--border))' }}>
          {hasChildren ? (
            <div className="p-2 space-y-2">
              {nodeChildren.map((child) => (
                <SkillTreeNodePreview
                  key={child.itemId}
                  node={child}
                  treeData={treeData}
                  expanded={expanded}
                  onToggle={onToggle}
                  depth={depth + 1}
                />
              ))}
            </div>
          ) : (
            <div className="p-3 space-y-3">
              {node.details.map((detail) => (
                <div
                  key={detail.itemId}
                  className="p-3 rounded-md bg-muted/30 border"
                  style={{ borderColor: 'hsl(var(--border))' }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{detail.name}</div>
                      <p className="text-xs text-muted-foreground mt-1">{detail.successCriteria}</p>
                      <div className="flex items-center gap-2 mt-2 text-xs">
                        <span className="px-2 py-0.5 rounded bg-muted text-muted-foreground">
                          {detail.materialType}
                        </span>
                        <span className="text-muted-foreground">{detail.materialName}</span>
                      </div>
                    </div>
                    {detail.link && (
                      <a
                        href={detail.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80 flex-shrink-0 p-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const treeNodeShape = {
  itemId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  details: PropTypes.arrayOf(
    PropTypes.shape({
      itemId: PropTypes.string,
      name: PropTypes.string,
      successCriteria: PropTypes.string,
      materialName: PropTypes.string,
      materialType: PropTypes.string,
      link: PropTypes.string,
    }),
  ).isRequired,
  children: PropTypes.arrayOf(PropTypes.string).isRequired,
};

SkillTreeNodePreview.propTypes = {
  node: PropTypes.shape(treeNodeShape).isRequired,
  treeData: PropTypes.arrayOf(PropTypes.shape(treeNodeShape)).isRequired,
  expanded: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  depth: PropTypes.number,
};

SkillTreeNodePreview.defaultProps = {
  depth: 0,
};

function SkillsPreview({
  hardSkillsData,
  softSkillsData,
  growLevels,
  selectedLvls,
  setSelectedLvls,
}) {
  const [expandedHardSkills, setExpandedHardSkills] = useState([]);
  const [expandedSoftSkills, setExpandedSoftSkills] = useState([]);

  // Get root nodes (nodes that are not children of any other node)
  const rootHardSkills = useMemo(
    () =>
      hardSkillsData.filter(
        (i) => !hardSkillsData.some((item) => item.children.includes(i.itemId)),
      ),
    [hardSkillsData],
  );

  const rootSoftSkills = useMemo(
    () =>
      softSkillsData.filter(
        (i) => !softSkillsData.some((item) => item.children.includes(i.itemId)),
      ),
    [softSkillsData],
  );

  // Count unique tasks
  const hardSkillDetailsCount = useMemo(() => {
    const skillNamesSet = new Set();
    hardSkillsData.forEach((skill) => {
      skill.details.forEach((detail) => {
        skillNamesSet.add(detail.name);
      });
    });
    return skillNamesSet.size;
  }, [hardSkillsData]);

  const softSkillDetailsCount = useMemo(() => {
    const skillNamesSet = new Set();
    softSkillsData.forEach((skill) => {
      skill.details.forEach((detail) => {
        skillNamesSet.add(detail.name);
      });
    });
    return skillNamesSet.size;
  }, [softSkillsData]);

  // Expand/Collapse all
  const handleHardSkillsExpand = useCallback(() => {
    setExpandedHardSkills(
      expandedHardSkills.length ? [] : hardSkillsData.map((item) => item.itemId),
    );
  }, [expandedHardSkills.length, hardSkillsData]);

  const handleSoftSkillsExpand = useCallback(() => {
    setExpandedSoftSkills(
      expandedSoftSkills.length ? [] : softSkillsData.map((item) => item.itemId),
    );
  }, [expandedSoftSkills.length, softSkillsData]);

  // Toggle individual nodes
  const handleHardSkillToggle = useCallback((itemId) => {
    setExpandedHardSkills((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId],
    );
  }, []);

  const handleSoftSkillToggle = useCallback((itemId) => {
    setExpandedSoftSkills((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId],
    );
  }, []);

  const handleLevelToggle = (levelId) => {
    if (selectedLvls.includes(levelId)) {
      setSelectedLvls(selectedLvls.filter((id) => id !== levelId));
    } else {
      setSelectedLvls([...selectedLvls, levelId]);
    }
  };

  return (
    <div className="space-y-6">
      {/* Level Filter */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-muted-foreground">Filter by Level:</span>
        <div className="flex flex-wrap gap-2">
          {growLevels.map((level) => {
            const isSelected = selectedLvls.includes(level._id);
            return (
              <button
                key={level._id}
                type="button"
                onClick={() => handleLevelToggle(level._id)}
                className={cn(
                  'px-3 py-1.5 rounded-full text-xs font-medium transition-all',
                  isSelected
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80',
                )}
              >
                {level.key}
              </button>
            );
          })}
        </div>
      </div>

      {/* Skills Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hard Skills */}
        <Card style={{ border: '1px solid hsl(var(--border))' }}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Cpu className="h-4 w-4" />
                Hard Skills
                <span className="ml-2 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs">
                  {hardSkillDetailsCount}
                </span>
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={handleHardSkillsExpand}>
                {expandedHardSkills.length ? 'Collapse' : 'Expand'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="max-h-[50vh] overflow-y-auto space-y-2">
            {rootHardSkills.length > 0 ? (
              rootHardSkills.map((node) => (
                <SkillTreeNodePreview
                  key={node.itemId}
                  node={node}
                  treeData={hardSkillsData}
                  expanded={expandedHardSkills}
                  onToggle={handleHardSkillToggle}
                />
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No hard skills found
              </div>
            )}
          </CardContent>
        </Card>

        {/* Soft Skills */}
        <Card style={{ border: '1px solid hsl(var(--border))' }}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Heart className="h-4 w-4" />
                Soft Skills
                <span className="ml-2 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs">
                  {softSkillDetailsCount}
                </span>
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={handleSoftSkillsExpand}>
                {expandedSoftSkills.length ? 'Collapse' : 'Expand'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="max-h-[50vh] overflow-y-auto space-y-2">
            {rootSoftSkills.length > 0 ? (
              rootSoftSkills.map((node) => (
                <SkillTreeNodePreview
                  key={node.itemId}
                  node={node}
                  treeData={softSkillsData}
                  expanded={expandedSoftSkills}
                  onToggle={handleSoftSkillToggle}
                />
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No soft skills found
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

SkillsPreview.propTypes = {
  hardSkillsData: PropTypes.arrayOf(
    PropTypes.shape({
      itemId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      details: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
      children: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ).isRequired,
  softSkillsData: PropTypes.arrayOf(
    PropTypes.shape({
      itemId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      details: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
      children: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ).isRequired,
  growLevels: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      key: PropTypes.string.isRequired,
    }),
  ).isRequired,
  selectedLvls: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedLvls: PropTypes.func.isRequired,
};

export default SkillsPreview;
